import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { ListOptions } from './list-options';
import { PersistorService, StoreService, TrailService } from 'core/services';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'lms-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public serviceProvider;
  @Input() public options: any;
  @Input() listId: string;
  @Input() public refreshList: EventEmitter<boolean>;
  @Output() collectionUpdated: EventEmitter<any[]> = new EventEmitter<any[]>();
  listOptions: ListOptions;
  public listSubscription: any;
  public selfOptionsUpdate = false;
  private routeSubscription: Subscription = new Subscription();

  constructor(private trailService: TrailService, private activatedRoute: ActivatedRoute) {}

  onError() {}

  onComplete() {}

  ngOnInit() {
    StoreService.create(this.listId + '_DOCS');
    this.listSubscription = StoreService.get(this.listId).subscribe((newListOptions) => {
      if (this.selfOptionsUpdate) {
        this.selfOptionsUpdate = false;
        return;
      }
      this.listOptions = Object.assign({}, this.listOptions, newListOptions);
      PersistorService.set(this.listId, this.listOptions);

      if (this.listOptions.routeLoadName && !this.listOptions.docs.length) {
        this.routeSubscription = this.fetchPreLoaded$(this.listOptions.routeLoadName).subscribe();
        this.routeSubscription.unsubscribe();
      } else if (this.listOptions.active && !this.selfOptionsUpdate) {
        this.fetch();
      }
    });

    if (this.refreshList) {
      this.refreshList.subscribe(() => {
        this.fetch();
      });
    }
  }

  ngOnChanges() {
    const listOptions = Object.assign(
      {},
      ListOptions.getValues(this.listId, this.trailService.getTrailFor(this.listId)),
      this.options
    );
    StoreService.update(this.listId, listOptions);
  }

  ngOnDestroy() {
    this.listSubscription.unsubscribe();
    StoreService.remove(this.listId);
  }

  fetch() {
    const { page, limit, sort, search, filters, aggregates, populate, clientId, locale } = this.listOptions;
    const paginationOptions = { page, limit, sort, search, aggregates, populate, clientId, locale };
    if (filters) {
      filters.forEach((value, key) => (paginationOptions[key] = value));
    }
    this.serviceProvider.fetch(paginationOptions).then((response) => {
      if (response) {
        if (this.listOptions.aggregates && this.listOptions.aggregates.length) {
          response.aggregates = this.listOptions.aggregates;
        }
        this.setCollection(response);
      } else {
        if (page > 1) {
          return this.fetch();
        }
        return this.setCollection({ ...this.listOptions, page: 0, pages: 0, total: 0, docs: [] });
      }
    });
  }

  setCollection(listOptions: ListOptions) {
    if (!listOptions) {
      this.collectionUpdated.next(this.listOptions.docs);
    } else {
      this.collectionUpdated.next(listOptions.docs);
    }
    this.listOptions = { ...this.listOptions, ...listOptions, ...this.isEmptyDocs(this.options) };

    StoreService.update(this.listId + '_DOCS', this.listOptions.docs);
    this.selfOptionsUpdate = true;
    StoreService.update(this.listId, this.listOptions);
  }

  /**
   * Check if the request is empty
   * @param listOptions
   * @private
   */
  private isEmptyDocs(listOptions: ListOptions): Object {
    return {
      hideEmptyLabel: listOptions?.hideEmptyLabel
        ? listOptions.hideEmptyLabel
        : !(this.listOptions.docs || listOptions.docs)
    };
  }

  /**
   * Get the docs from the activated route
   * @private
   */
  private fetchPreLoaded$(label: string): Observable<any> {
    return this.activatedRoute.data.pipe(
      tap((data) => {
        this.setCollection(data[label]);
      })
    );
  }
}
